import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { DisclaimerBanner } from '../components/Disclaimer';
import Bubble from '../components/Bubble';
import {
  HouseDivider,
  BubbleLandlordIcon,
  BubbleSearchIcon,
  BubbleTenantIcon,
  StatFirmsIcon,
  StatReviewsIcon,
  StatRentersIcon,
  StatManagersIcon,
} from '../components/HeroIcons';

// Hero background photo: Minh Pham on Unsplash (@minhphamdesign)
// https://unsplash.com/photos/OtXADkUh3-I — license: Unsplash (free use, no attribution required)
const HERO_BG_URL = 'https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?w=2000&q=80&auto=format&fit=crop';

export default function Home({ user }) {
  const isManager = user?.role === 'manager';
  const isTenant  = user?.role === 'tenant';
  const [query, setQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [stats, setStats] = useState({ managers: 0, tenantReviews: 0, managerReviews: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function loadData() {
      const { data: mgrs } = await supabase.from('managers').select('id');
      const { data: tenantRevs } = await supabase.from('reviews').select('id').eq('profile_type', 'manager');
      const { data: managerRevs } = await supabase.from('reviews').select('id').eq('profile_type', 'tenant');
      const tCount = tenantRevs?.length || 0;
      const mCount = managerRevs?.length || 0;
      setStats({ managers: mgrs?.length || 0, tenantReviews: tCount, managerReviews: mCount, total: tCount + mCount });
      setLoading(false);
    }
    loadData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate('/search?q=' + encodeURIComponent(query.trim()));
  };

  return (
    <div className="page-wrap" style={{ background: '#F5EFE3', paddingBottom: 0 }}>

      {/* Hero — warm interior bg with cream tint overlay */}
      <div
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(245,239,227,0.35) 0%, rgba(245,239,227,0.82) 35%, rgba(245,239,227,0.82) 65%, rgba(245,239,227,0.35) 100%), linear-gradient(180deg, rgba(245,239,227,0.0) 0%, rgba(245,239,227,0.55) 100%), url('${HERO_BG_URL}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: 'clamp(44px, 8vw, 80px) 16px clamp(28px, 5vw, 48px)',
          textAlign: 'center',
        }}
      >
        <h1
          style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: 'clamp(52px, 13vw, 96px)',
            color: '#0F2C4E',
            letterSpacing: '-0.02em',
            lineHeight: 1,
            margin: 0,
            fontWeight: 400,
          }}
        >
          Renter<span style={{ color: '#B8893A' }}>Rep</span>
        </h1>

        <HouseDivider width={320} />

        <p
          style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: 'clamp(18px, 3.8vw, 24px)',
            color: '#0F2C4E',
            margin: '0 auto 40px',
            fontWeight: 400,
            letterSpacing: '0.01em',
          }}
        >
          Where Reputation is Key
        </p>

        {/* Logged-out: three cream bubbles. Logged-in: two pill
            buttons on either side (a user only reviews the other side). */}
        {!user ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: 'clamp(12px, 6vw, 64px)',
              maxWidth: 720,
              margin: '0 auto',
              padding: '0 8px',
            }}
          >
            <Bubble
              icon={<BubbleLandlordIcon />}
              label={'Review\nProperty Management'}
              subtext="Share your experience and help others."
              onClick={() => navigate('/find?type=manager')}
            />
            <Bubble
              icon={<BubbleSearchIcon />}
              label="Search"
              subtext="Find insights and reviews that matter."
              onClick={() => setShowSearch(s => !s)}
            />
            <Bubble
              icon={<BubbleTenantIcon />}
              label={'Review a\nTenant'}
              subtext="Build stronger, more trustworthy communities."
              onClick={() => navigate('/find?type=tenant')}
            />
          </div>
        ) : (
          <div
            className="home-pill-row"
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              columnGap: '3rem',
              rowGap: 10,
              maxWidth: 760,
              margin: '0 auto',
              padding: '0 16px',
              flexWrap: 'wrap',
            }}
          >
            <HomePill
              label="Search reviews"
              icon={<BubbleSearchIcon size={20} />}
              onClick={() => setShowSearch(s => !s)}
            />
            {isManager && (
              <HomePill
                label="Review a Tenant"
                icon={<BubbleTenantIcon size={20} />}
                onClick={() => navigate('/find?type=tenant')}
              />
            )}
            {isTenant && (
              <HomePill
                label="Review Property Management"
                icon={<BubbleLandlordIcon size={20} />}
                onClick={() => navigate('/find?type=manager')}
              />
            )}
          </div>
        )}

        {/* Search bar */}
        {showSearch && (
          <form
            onSubmit={handleSearch}
            style={{ maxWidth: 440, margin: '28px auto 0', position: 'relative' }}
          >
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={isManager ? 'Search tenant' : isTenant ? 'Search property management company' : 'Search property management, tenant, or address...'}
              autoFocus
              style={{
                width: '100%',
                padding: '14px 110px 14px 18px',
                borderRadius: 12,
                border: '1.5px solid rgba(15,44,78,0.25)',
                background: '#FAF6EC',
                color: '#2C2C2C',
                fontSize: 14,
                outline: 'none',
              }}
            />
            <button
              type="submit"
              style={{
                position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                background: '#0F2C4E', color: '#fff', border: 'none',
                padding: '8px 18px', borderRadius: 999, fontSize: 13, fontWeight: 700, letterSpacing: '0.02em',
                cursor: 'pointer', boxShadow: '0 2px 8px rgba(15,44,78,0.25)',
              }}
            >
              Search
            </button>
          </form>
        )}

        {/* Credit-reports announcement pill */}
        <div style={{ marginTop: 36, padding: '0 16px' }}>
          <button
            onClick={() => navigate(!user ? '/signup' : (isManager ? '/search?type=tenant' : '/pricing'))}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              background: '#0F2C4E',
              border: 'none',
              borderRadius: 999,
              padding: '8px 18px',
              cursor: 'pointer',
              boxShadow: '0 8px 20px rgba(15,44,78,0.22)',
            }}
          >
            <span
              style={{
                background: '#C9972A', color: '#fff',
                fontSize: 10, fontWeight: 800, letterSpacing: '0.08em',
                padding: '3px 8px', borderRadius: 999,
              }}
            >
              NEW
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>
              Tenant credit reports
            </span>
          </button>
        </div>
      </div>

      {/* Stats bar — forest green, gold line icons, vertical separators */}
      <div style={{ background: '#0F2C4E' }}>
        <div
          style={{
            maxWidth: 880,
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            height: 56,
          }}
        >
          {[
            { num: loading ? '…' : stats.total, label: 'Reviews', Icon: StatReviewsIcon },
            { num: loading ? '…' : stats.tenantReviews, label: 'By Renters', Icon: StatRentersIcon },
            { num: loading ? '…' : stats.managerReviews, label: 'By Property Managers', Icon: StatManagersIcon },
          ].map((s, i, arr) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '0 6px',
                borderRight: i < arr.length - 1 ? '1px solid rgba(184,137,58,0.35)' : 'none',
              }}
            >
              <s.Icon />
              <span style={{ fontSize: 16, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{s.num}</span>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.03em' }}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* How It Works pill + cards (cream-tinted) */}
      <div style={{ background: '#F5EFE3', padding: '40px 16px 16px', textAlign: 'center' }}>
        <span
          style={{
            display: 'inline-block',
            background: '#0F2C4E',
            color: '#fff', fontSize: 16, fontWeight: 700,
            padding: '12px 36px', borderRadius: 50,
            boxShadow: '0 4px 16px rgba(15,44,78,0.25)',
          }}
        >
          How RenterRep Works
        </span>
        <p style={{ fontSize: 13, color: '#6B6358', marginTop: 10 }}>Transparent. Verified. Fair for everyone.</p>
      </div>

      <div style={{ background: '#F5EFE3', padding: '16px 16px 32px' }}>
        <div style={{ maxWidth: 480, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            { step: '01', icon: '📝', title: 'Sign up for free', desc: 'Create your account in 30 seconds.' },
            { step: '02', icon: '🔍', title: 'Search & verify', desc: 'Look up any property management or tenant before you commit.' },
            { step: '03', icon: '⭐', title: 'Write a review', desc: 'Rate any manager or tenant. Always free to post.' },
          ].map((item, i) => (
            <div
              key={i}
              style={{
                background: '#FAF6EC',
                borderRadius: 16,
                border: '1px solid rgba(184,137,58,0.18)',
                padding: '16px',
                display: 'flex', gap: 14, alignItems: 'flex-start',
                boxShadow: '0 2px 8px rgba(120,90,30,0.06)',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#0F2C4E', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{item.icon}</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#B8893A', letterSpacing: 0.5 }}>{item.step}</div>
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0F2C4E', marginBottom: 4 }}>{item.title}</div>
                <div style={{ fontSize: 13, color: '#6B6358', lineHeight: 1.6 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA pill + buttons */}
      <div style={{ background: '#F5EFE3', padding: '36px 16px 48px', textAlign: 'center' }}>
        <span
          style={{
            display: 'inline-block',
            background: '#B8893A',
            color: '#fff', fontSize: 16, fontWeight: 700,
            padding: '12px 36px', borderRadius: 50,
            boxShadow: '0 4px 16px rgba(184,137,58,0.32)',
            marginBottom: 12,
          }}
        >
          Ready to get started?
        </span>
        <p style={{ fontSize: 13, color: '#6B6358', marginBottom: 20 }}>Writing reviews is always free. Sign up in 30 seconds.</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/signup')} style={{ background: '#0F2C4E', color: '#fff', border: 'none', padding: '12px 28px', borderRadius: 50, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            Sign Up Free
          </button>
          <button onClick={() => navigate('/pricing')} style={{ background: 'transparent', color: '#0F2C4E', border: '1.5px solid #0F2C4E', padding: '12px 28px', borderRadius: 50, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
            View Pricing
          </button>
        </div>
      </div>

      <div className="container" style={{ paddingTop: 8, paddingBottom: 32 }}>
        <DisclaimerBanner />
      </div>

    </div>
  );
}

function HomePill({ label, icon, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        background: '#FAF6EC',
        color: '#0F2C4E',
        border: '1px solid rgba(184,137,58,0.45)',
        padding: '8px 18px',
        borderRadius: 999,
        fontSize: 13, fontWeight: 700,
        letterSpacing: '0.02em',
        cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(120,90,30,0.10)',
        width: 280, boxSizing: 'border-box',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}>{icon}</span>
      {label}
    </button>
  );
}
